/** HTTP boundary for the journal API.
 *
 *  Same-origin by design: `/v1` is proxied to the loopback API in development
 *  and served by Caddy in production. The journal API has no CORS layer, so
 *  never point this at another origin.
 */

export interface Problem {
  type: string;
  title: string;
  status: number;
  code: string;
  request_id: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The machine-readable code the CLI and TUI print for the same failure. */
  get code(): string {
    return this.problem?.code ?? `http_${this.status}`;
  }

  /** A revision race: the `If-Match` did not match the current revision.
   *
   *  This is **412**, not 409. The API reserves 409 for a reused idempotency key
   *  carrying a different request, and uses `PRECONDITION_FAILED` with code
   *  `revision_conflict` for a lost update. The design document labels its
   *  conflict screen `CONFLICT · 409`, which is the wrong status for the case it
   *  describes — checking 409 here made the whole conflict surface unreachable.
   */
  get isRevisionConflict(): boolean {
    return this.status === 412 || this.problem?.code === 'revision_conflict';
  }

  /** The same `Idempotency-Key` replayed with a different body. Not a lost
   *  update — a client bug, or a key reused across two logical mutations. */
  get isKeyReused(): boolean {
    return this.status === 409 && this.problem?.code === 'idempotency_key_reused';
  }
}

/** A failure as one line, for a banner or a toast.
 *
 *  `ApiError.message` is already `"Validation failed (validation_failed)"`, so
 *  the seventeen call sites that appended `· ${error.code}` were all printing
 *  the code twice — `Validation failed (validation_failed) · validation_failed`
 *  on every error surface in the app. The code has to be shown, because the CLI
 *  and TUI print the same one and cross-client debugging depends on it, but once.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    // A body can be problem+json shaped and still be missing `title` — which
    // rendered as `! undefined · upstream_unavailable`. The code is the part
    // that matters; the title is the readable half and falls back to the status.
    const title = error.problem?.title?.trim();
    return `${title || `HTTP ${error.status}`} · ${error.code}`;
  }
  return error instanceof Error ? error.message : String(error);
}

export interface Session {
  /** Base URL. Empty string means same-origin, which is the deployed shape. */
  baseUrl: string;
  token: string;
}

export interface Fetched<T> {
  body: T;
  /** Strong ETag, when the resource carries one. Needed for `If-Match`. */
  etag: string | null;
}

interface RequestOptions {
  method?: string;
  /** JSON unless it is a `Blob`, which is sent as-is under its own
   *  `Content-Type` — the photo upload route takes the image itself as the
   *  body rather than a JSON envelope or a multipart form. */
  body?: unknown;
  /** Sent as `If-Match` on revision-guarded mutations. */
  etag?: string | null;
  /** Sent as `Idempotency-Key`. Mint once per logical mutation and persist it
   *  with the queued operation — never generate it at send time, or a retry
   *  becomes a duplicate. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function request<T>(
  session: Session,
  path: string,
  options: RequestOptions = {},
): Promise<Fetched<T>> {
  const headers: Record<string, string> = {
    authorization: `Bearer ${session.token}`,
    accept: 'application/json',
  };
  const raw = options.body instanceof Blob;
  if (raw) {
    // The blob's own type. A blob with no type would upload as
    // `application/octet-stream` and be refused, which is correct: the API will
    // not store bytes whose type nobody has stated.
    headers['content-type'] = (options.body as Blob).type;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (options.etag) headers['if-match'] = options.etag;
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  const response = await fetch(`${session.baseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: raw
      ? (options.body as Blob)
      : options.body === undefined
        ? undefined
        : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!response.ok) {
    let problem: Problem | null = null;
    try {
      const text = await response.text();
      if (text) problem = JSON.parse(text) as Problem;
    } catch {
      // A non-JSON error body is still an error; the status carries the meaning.
    }
    throw new ApiError(
      response.status,
      problem,
      problem ? `${problem.title} (${problem.code})` : `HTTP ${response.status}`,
    );
  }

  const etag = response.headers.get('etag');
  if (response.status === 204) return { body: undefined as T, etag };
  return { body: (await response.json()) as T, etag };
}
